import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import useClipboard from 'react-use-clipboard';

import {
  TRANSACTION_ITEM_TYPE,
  SUBSCRIPTION_ITEM_TYPE,
} from '../../../../constants/transactions';
import {
  getBasenameFromEntity,
  getProductBasenames,
} from '../../../../util/role-definitions';
import { formatPriceForDisplay } from '../../../../util/format-money';
import {
  getSubscriptionItemTypeLabel,
  getTransactionItemRefundedTotal,
} from '../../../../util/transactions';
import { DATE_FORMAT_WITH_TIME } from '../../../../constants/dates';
import Button from '../../../global/Button';
import { CURRENCY_SYMBOLS } from '../../../../constants/currency';

const propTypes = {
  transactionItem: PropTypes.shape({
    id: PropTypes.string,
    transaction: PropTypes.shape({
      id: PropTypes.string,
      uuid: PropTypes.string,
      roes_order_id: PropTypes.string,
      print_partner: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        roes_info: PropTypes.shape({
          lab_id: PropTypes.string,
        }),
      }),
    }),
    total_price: PropTypes.shape({
      value: PropTypes.number,
    }),
    item_type: PropTypes.oneOf(Object.keys(TRANSACTION_ITEM_TYPE)),
    subscription_item_type: PropTypes.oneOf(Object.keys(SUBSCRIPTION_ITEM_TYPE))
      .isRequired,
    album: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    album_folder: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    identity: PropTypes.shape({
      id: PropTypes.string,
    }),
    vendor_state_tax_price: PropTypes.shape({
      value: PropTypes.number,
    }),
    vendor_local_tax_price: PropTypes.shape({
      value: PropTypes.number,
    }),
    tax_total_price: PropTypes.shape({
      value: PropTypes.number,
    }),
  }),
  basename: PropTypes.oneOf(getProductBasenames()),
};

const SubscriptionTransactionDetails = ({ transactionItem, basename }) => {
  const { album, album_folder: albumFolder } = transactionItem;
  const {
    uuid: orderNumber,
    order_number: orderShorthand,
    roes_order_id: roesOrderNumber,
    print_partner: printPartner,
  } = transactionItem?.transaction ?? {};

  const [orderNumberCopied, setOrderNumberCopied] = useClipboard(orderNumber);
  const [orderShorthandCopied, setOrderShorthandCopied] = useClipboard(
    orderShorthand,
  );
  const [roesOrderNumberCopied, setRoesOrderNumberCopied] = useClipboard(
    roesOrderNumber,
  );

  return (
    <div>
      <table className="table table-striped mb-3">
        <tbody>
          <tr>
            <td>
              {' '}
              Order Number{' '}
              <Button btnStyle="link" onClick={setOrderNumberCopied}>
                {orderNumberCopied ? 'Copied!' : 'Copy'}
              </Button>
            </td>
            <td>{orderNumber}</td>
          </tr>
          {orderShorthand && (
            <tr>
              <td>
                Order Shorthand{' '}
                <Button btnStyle="link" onClick={setOrderShorthandCopied}>
                  {orderShorthandCopied ? 'Copied!' : 'Copy'}
                </Button>
              </td>
              <td>{orderShorthand}</td>
            </tr>
          )}
          {roesOrderNumber && (
            <tr>
              <td>
                ROES Order Number{' '}
                <Button btnStyle="link" onClick={setRoesOrderNumberCopied}>
                  {roesOrderNumberCopied ? 'Copied!' : 'Copy'}
                </Button>
              </td>
              <td>{roesOrderNumber}</td>
            </tr>
          )}{' '}
          {printPartner && (
            <tr>
              <td>Print Partner</td>
              <td>
                {printPartner.name}
                {printPartner?.roes_info?.lab_id
                  ? ` (Lab ID: ${printPartner.roes_info.lab_id}})`
                  : ''}
              </td>
            </tr>
          )}
          <tr>
            <td> Transaction Type </td>
            <td> Photo Purchase </td>
          </tr>
          <tr>
            <td> Date </td>
            <td>
              {moment(transactionItem.created_at).format(DATE_FORMAT_WITH_TIME)}
            </td>
          </tr>
          <tr>
            <td> Item Type </td>
            <td>
              {getSubscriptionItemTypeLabel(
                transactionItem.subscription_item_type,
              )}
            </td>
          </tr>
          <tr>
            {transactionItem?.subscription_item_type ===
            SUBSCRIPTION_ITEM_TYPE.ALBUM ? (
              <Fragment>
                <td> Album </td>
                {album?.id ? (
                  <td>
                    <a
                      href={`/app/${
                        getBasenameFromEntity(album) ?? basename
                      }/albums/${album.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {album.name}
                    </a>
                  </td>
                ) : (
                  <td>{transactionItem?.album_name}</td>
                )}
              </Fragment>
            ) : (
              <Fragment>
                <td> Gallery </td>
                {albumFolder?.id ? (
                  <td>
                    <a
                      href={`/app/${
                        getBasenameFromEntity(albumFolder) ?? basename
                      }/folders/${albumFolder.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {albumFolder.name}
                    </a>
                  </td>
                ) : (
                  <td>{transactionItem?.album_folder_name}</td>
                )}
              </Fragment>
            )}
          </tr>
          <tr>
            <td> Identity </td>
            <td>{transactionItem?.identity_name}</td>
          </tr>
          <tr>
            <td> Amount Paid </td>
            <td>
              {(transactionItem?.total_price?.value ?? 0) >= 0 ? (
                <Fragment>
                  {
                    CURRENCY_SYMBOLS[
                      transactionItem?.total_price?.currency ?? 'USD'
                    ]
                  }
                  {formatPriceForDisplay(
                    transactionItem?.total_price?.value ?? 0,
                  )}
                </Fragment>
              ) : (
                'N/A'
              )}
            </td>
          </tr>
          {(transactionItem?.vendor_state_tax_price?.value ?? 0) > 0 && (
            <tr>
              <td> State Tax Collected </td>
              <td>
                {
                  CURRENCY_SYMBOLS[
                    transactionItem?.vendor_state_tax_price?.currency ?? 'USD'
                  ]
                }
                {formatPriceForDisplay(
                  transactionItem?.vendor_state_tax_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          {(transactionItem?.vendor_local_tax_price?.value ?? 0) > 0 && (
            <tr>
              <td> Local Tax Collected </td>
              <td>
                {
                  CURRENCY_SYMBOLS[
                    transactionItem?.vendor_local_tax_price?.currency ?? 'USD'
                  ]
                }
                {formatPriceForDisplay(
                  transactionItem?.vendor_local_tax_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          {(transactionItem?.tax_total_price?.value ?? 0) > 0 && (
            <tr>
              <td> Total Tax Collected </td>
              <td>
                {
                  CURRENCY_SYMBOLS[
                    transactionItem?.tax_total_price?.currency ?? 'USD'
                  ]
                }
                {formatPriceForDisplay(
                  transactionItem?.tax_total_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          <tr>
            <td> Amount Refunded </td>
            <td>
              {
                CURRENCY_SYMBOLS[
                  transactionItem?.total_price?.currency ?? 'USD'
                ]
              }
              {formatPriceForDisplay(
                getTransactionItemRefundedTotal(transactionItem),
              )}
            </td>
          </tr>
          <tr>
            <td>Final Amount</td>
            <td>
              {(transactionItem?.total_price?.value ?? 0) >= 0 ? (
                <Fragment>
                  {
                    CURRENCY_SYMBOLS[
                      transactionItem?.total_price?.currency ?? 'USD'
                    ]
                  }
                  {formatPriceForDisplay(
                    (transactionItem?.total_price?.value ?? 0) -
                      getTransactionItemRefundedTotal(transactionItem),
                  )}
                </Fragment>
              ) : (
                'N/A'
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

SubscriptionTransactionDetails.propTypes = propTypes;

export default SubscriptionTransactionDetails;
